import { MAX_TEXT_CHARACTERS, slugName } from "@phone-booth/content/layout";
import { useEffect, useState } from "preact/hooks";
import {
  addTypedMessage,
  categoryTitle,
  editMessage,
  getVoices,
  type Library,
  type Message,
  previewSpeech,
  type VoiceList,
} from "./api.ts";
import { audioDataUrl } from "./audio.ts";
import { message } from "./errors.ts";
import { Check, Clock } from "./icons.tsx";
import { Button, Callout, Field, Pill, ScreenHead, ScreenTitle } from "./ui.tsx";
import { useAction } from "./use-action.ts";
import { loadVoiceSamples, voiceSampleUrl } from "./voice-samples.ts";
import {
  defaultVoiceId,
  isVoiceEnabled,
  offeredVoices,
  voiceCharacter,
  voiceInitial,
  voiceLabel,
} from "./voices.ts";

interface Props {
  library: Library;
  category: string;
  /** The stored message this screen reopens, or absent when it writes a new one. */
  editing?: Message | undefined;
  onBack: () => void;
  onSaved: (id: string) => void;
}

/**
 * Writes a message, and reopens one. An edit keeps the message's id, so its
 * audio stays under the same keys and the row keeps the day it first appeared;
 * see IMPLEMENTATION.md 10.13.
 */
export function TypeMessageScreen({
  library,
  category,
  editing,
  onBack,
  onSaved,
}: Props) {
  const [voices, setVoices] = useState<VoiceList | null>(null);
  const [title, setTitle] = useState(editing?.title ?? "");
  const [text, setText] = useState(editing?.text ?? "");
  const [voice, setVoice] = useState("");
  // Whether a person chose this voice, as opposed to the screen preselecting
  // one. It only matters for a message that has no stored voice: preselecting
  // the default there is a stand-in, not a change anybody made.
  const [picked, setPicked] = useState(false);
  const [samples, setSamples] = useState<ReadonlySet<string>>(new Set());
  const [heard, setHeard] = useState<string | null>(null);
  const { busy, error, setError, run } = useAction();

  const topic = categoryTitle(library, category);

  useEffect(() => {
    getVoices().then(
      (list) => {
        setVoices(list);
        // A stored voice wins, even one the booth has curated out: the card is
        // drawn anyway rather than swapped for something nobody chose.
        setVoice(editing?.voice ?? defaultVoiceId(list));
      },
      (reason: unknown) => setError(message(reason)),
    );
    loadVoiceSamples().then(setSamples, () => undefined);
  }, [setError, editing?.voice]);

  const wanted = title.trim();
  const words = text.trim();
  const over = text.length > MAX_TEXT_CHARACTERS;
  const credits = voices?.credits;

  /**
   * Nothing recorded which voice read this message, so there is no "the same
   * voice" to compare the picker against. Saving settles it, and that means
   * reading the words again.
   */
  const voiceUnknown = editing !== undefined && editing.voice === undefined;

  /** A save that has to speak the text again — the half that spends credits. */
  const rerecords =
    editing === undefined ||
    words !== (editing.text ?? "") ||
    (voiceUnknown ? picked : voice !== "" && voice !== editing.voice);

  const changed = editing === undefined || rerecords || wanted !== editing.title;

  /**
   * A message's id comes from its title, and saving under an id the category
   * already holds replaces that message — which is how an edit works. A new
   * message must not reach that path by being named the same as an existing
   * one, or a volunteer picking an obvious title would silently overwrite
   * somebody's recording.
   */
  const clashes =
    editing === undefined &&
    wanted !== "" &&
    (library.categories
      .find((entry) => entry.category === category)
      ?.messages.some((entry) => entry.id === slugName(wanted)) ??
      false);

  const ready = wanted !== "" && words !== "" && !over && changed && !clashes;

  /** Every synthesis moves the credit count, so re-read it either way. */
  function metered(work: () => Promise<void>): Promise<boolean> {
    return run(work).finally(() => getVoices().then(setVoices, () => undefined));
  }

  /**
   * The voice to save in. Nothing is selected until the list arrives, and an
   * edit falls back to the voice the message was already read in — otherwise a
   * save made in that moment, or after the list failed to load, would quietly
   * swap a message onto the engine's default.
   */
  function chosen(): string | undefined {
    return voice === "" ? editing?.voice : voice;
  }

  function pick(id: string): void {
    setVoice(id);
    setPicked(true);
  }

  function speak(): void {
    void metered(async () => {
      const spoken = await previewSpeech(words, chosen());
      setHeard(audioDataUrl(spoken.audio, spoken.contentType));
    });
  }

  function save(event: Event): void {
    event.preventDefault();
    if (editing !== undefined && !rerecords) {
      // Only the title moved. Reading the message again to change what the
      // list calls it would spend credits on something no caller ever hears.
      void run(async () => {
        await editMessage(category, editing.id, { title: wanted });
        onSaved(editing.id);
      });
      return;
    }
    void metered(async () => {
      const written = await addTypedMessage({
        category,
        // The same id is what makes this an edit rather than a second message.
        ...(editing === undefined ? {} : { id: editing.id }),
        title: wanted,
        text: words,
        voice: chosen(),
        // An edit leaves the message as enabled or disabled as it was; a new
        // one arrives disabled so a person hears it before callers do.
        active: editing?.active ?? false,
      });
      onSaved(written.message.id);
    });
  }

  return (
    <>
      <ScreenHead onBack={onBack} disabled={busy} />
      <ScreenTitle>{editing ? "Edit message" : "New message"}</ScreenTitle>
      <p class="lede meta-tight">for {topic}</p>

      <form onSubmit={save}>
        <Field
          id="typed-title"
          label="Title"
          help={
            clashes
              ? "There is already a message called that. Pick a different title."
              : undefined
          }
        >
          <input
            id="typed-title"
            type="text"
            placeholder="Tuesday's joke"
            value={title}
            disabled={busy}
            aria-invalid={clashes}
            onInput={(event) => setTitle((event.target as HTMLInputElement).value)}
          />
        </Field>

        <Field
          id="typed-text"
          label="What should it say?"
          counter={{ used: text.length, limit: MAX_TEXT_CHARACTERS }}
        >
          <textarea
            id="typed-text"
            rows={5}
            value={text}
            disabled={busy}
            onInput={(event) => setText((event.target as HTMLTextAreaElement).value)}
          />
        </Field>

        <fieldset class="voices">
          <legend class="field-label">Choose a voice</legend>
          <div class="voice-grid">
            {(voices ? offeredVoices(voices, voice) : []).map((option) => (
              <VoiceCard
                key={option.id}
                id={option.id}
                face={voiceInitial(option.name)}
                name={voiceLabel(option.name)}
                description={voiceCharacter(option.description)}
                isDefault={option.name === voices?.defaultVoice}
                disabled={voices !== null && !isVoiceEnabled(voices, option.id)}
                selected={voice === option.id}
                busy={busy}
                onPick={() => pick(option.id)}
                onHear={
                  samples.has(option.id)
                    ? () => {
                        pick(option.id);
                        setHeard(voiceSampleUrl(option.id));
                      }
                    : undefined
                }
              />
            ))}
          </div>
          {voiceUnknown && (
            <p class="meta">
              We don't know which voice read this message. If you change the words, it
              will use the voice you pick here.
            </p>
          )}
        </fieldset>

        {credits && (
          <p class="footnote">
            <Clock />
            <span>
              {(credits.limit - credits.used).toLocaleString("en-US")} of{" "}
              {credits.limit.toLocaleString("en-US")} characters of voice left this
              month.
            </span>
          </p>
        )}

        <div class="stack">
          <Button disabled={busy || words === "" || over} onClick={speak}>
            Preview this message
          </Button>
          <Button
            tone="primary"
            type="submit"
            busy={busy}
            busyLabel="Saving…"
            disabled={!ready}
          >
            {editing ? "Save changes" : "Save message"}
          </Button>
        </div>
        <p class="meta">{creditNote(editing !== undefined, rerecords)}</p>
      </form>

      {heard && (
        <div class="player player-block">
          {/* biome-ignore lint/a11y/useMediaCaption: this is the words in the box above */}
          <audio controls autoPlay src={heard} />
        </div>
      )}
      {error && <Callout tone="bad" title={error} onDismiss={() => setError(null)} />}
    </>
  );
}

/**
 * What previewing and saving cost. A save that only changes the title reads
 * nothing, and the line under the buttons has to stop short of implying it does.
 */
function creditNote(editing: boolean, rerecords: boolean): string {
  if (!editing) return "Previewing and saving each use voice credits.";
  if (rerecords) {
    return "Previewing and saving each use voice credits, because both read the message aloud.";
  }
  return "Previewing uses voice credits. Renaming does not.";
}

interface VoiceProps {
  id: string;
  face: string;
  name: string;
  description: string;
  /** The engine's own default, said on the card so a person can see which it is. */
  isDefault: boolean;
  /** Curated out of the catalog, and only on screen because it is selected. */
  disabled: boolean;
  selected: boolean;
  busy: boolean;
  onPick: () => void;
  /** Absent when this voice has no sample file, which hides the button. */
  onHear?: (() => void) | undefined;
}

/**
 * A real radio under the card, so arrow keys move between voices without this
 * having to implement them.
 */
function VoiceCard({
  id,
  face,
  name,
  description,
  isDefault,
  disabled,
  selected,
  busy,
  onPick,
  onHear,
}: VoiceProps) {
  return (
    <div class={selected ? "voice-card voice-card-on" : "voice-card"}>
      <label class="voice-pick">
        <input
          type="radio"
          class="visually-hidden"
          name="voice"
          value={id}
          checked={selected}
          disabled={busy}
          onChange={onPick}
        />
        <span class="voice-face" aria-hidden="true">
          {face}
        </span>
        <span class="voice-text">
          <span class="voice-name">
            {name}
            {isDefault && <Pill tone="brand" label="Default" />}
            {disabled && <Pill tone="disabled" />}
          </span>
          <span class="voice-description">{description}</span>
        </span>
        {selected && (
          <span class="voice-check" aria-hidden="true">
            <Check />
          </span>
        )}
      </label>
      {onHear && (
        <Button tone="quiet" disabled={busy} onClick={onHear}>
          Hear voice
        </Button>
      )}
    </div>
  );
}
